import isMobile from 'is-mobile';
import { cloneElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, forwardRef, IconButton, StyleProps, usePopoverContext } from '@bonusly/design-system';

import { useLongPress } from '../../hooks/use-long-press';
import { useTrackReaction } from './hooks/use-track-reaction';
import { Reaction } from './lib/reaction';
import { useReactionContext } from './reaction-select-context';
import { activeStyle, buttonStyle, coinStyle, compactActiveStyle, compactButtonStyle } from './styles';

interface ReactionButtonSelectedProps extends StyleProps {
	reaction: Reaction;
}

export const ReactionButtonSelected = forwardRef<ReactionButtonSelectedProps, 'button'>(({ reaction, ...rest }, ref) => {
	const { t } = useTranslation('recognition');
	const { onClose } = usePopoverContext();
	const { isLoading, onToggle, removeReaction, variant } = useReactionContext();
	const track = useTrackReaction();

	const handleClickRemove = () => {
		track.remove(reaction.type);
		removeReaction(reaction);
		onClose();
	};

	// handles hover desktop and longpress on mobile
	const onLongPress = () => {
		if (isMobile()) onToggle();
	};

	const eventHandlers = useLongPress({
		onLongPress,
		onClick: handleClickRemove,
	});

	if (variant === 'compact') {
		return (
			<IconButton
				aria-label={t('reactions.actions.remove') ?? ''}
				data-testid="enhanced-reactions"
				ref={ref}
				isDisabled={isLoading}
				icon={cloneElement(<reaction.coin />, { height: 5, width: 5 })}
				{...eventHandlers}
				{...compactButtonStyle}
				{...compactActiveStyle}
				{...rest}
			/>
		);
	}

	return (
		<Button
			aria-label={t('reactions.actions.remove') ?? ''}
			data-testid="enhanced-reactions"
			ref={ref}
			isDisabled={isLoading}
			onClick={handleClickRemove}
			{...eventHandlers}
			{...buttonStyle}
			{...activeStyle}
			{...rest}
		>
			{cloneElement(<reaction.coin />, { height: 6, width: 6, ...coinStyle })}
			<Box as="span" display={{ base: 'none', xs: 'inline-block' }} ml={2}>
				{t(`reactions.name.${reaction.type}`)}
			</Box>
		</Button>
	);
});
